/* eslint-disable react/prop-types */
import { MagnifyingGlassPlusIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useState, useCallback } from 'react';
import { Controlled as ControlledZoom } from 'react-medium-image-zoom';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import pluralize from 'pluralize';
import { popItemFromConfig, addToCart } from '../../../actions/v3Order';
import cartUtils from '../../../utils/cart';
import formatUSD from '../../../utils/formatUSD';
import { isJpgImage } from '../../../utils/imageHelpers';
import productConfigUtils from '../../../utils/productConfig';
import { shouldBreakConfigLoop } from '../helpers/config';
import {
  isShippingRestrictedForParent,
  ypxHasHiddenChildren,
} from '../helpers/internationalRestriction';
import { extraConfigProducts, getProductByLocation } from '../helpers/store';
import useScreenSize, { MOBILE_L } from '../hooks/screen';
import useFoldOnSelect from '../hooks/useFoldOnSelect';
import useIsPhone from '../hooks/useIsPhone';
import CategoryPicker from '../shared/CategoryPicker';
import DoneScreen from './done';
import useImageIntentFriction from '../shared/protectedImage/useImageIntentFriction';
import useImageIntentZoomContent from '../shared/protectedImage/imageIntentZoomContent';
import { imageIntentGalleryOf } from '../shared/protectedImage/imageIntentGallery';
import {
  BackgroundNamePriceWrapper,
  CategoryButton,
  CategoryFoldWrapper,
  CategorySelector,
  ConfigScreenWrapper,
  Instruction,
  ItemImage,
  ItemImageBackgroundSelection,
  ItemImageBackgroundWrapper,
  ItemImageContainer,
  ItemPanel,
  ItemsGrid,
  ItemWrapper,
  PoseImage,
  PoseSlotBox,
  PoseSlotBoxFilled,
  PoseSlotsRow,
  ZoomButtonWrapper,
} from './productConfigStyles';
import SlotSelector from './slotSelector';
import RequiredProductForm from '../products/required-product-form/RequiredProductForm.jsx';
import ProductOptionsSelector from '../products/regular/subcomponents/ProductOptionsSelector';
import { setCurrentProduct } from '../../../reducers/productConfigSlice';
import {
  addToConfig,
  getPosesForSlot,
  getRequiredPoseCountForSlot,
} from './utils';
import {
  selectBackgroundsSatisfied,
  selectConfigFlowPages,
  selectConfiguredChildProducts,
} from '../../../selectors';
import { productHasGroupRecord } from '../products/imageRecords';
import UnsupportedProductConfig from '../products/UnsupportedProductConfig';

const DoneWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: min-content;
  min-width: 260px;
`;

// Palette matches the existing BannerMessage `alert` style — cream
// (#fcf8e3 / #faf2cc / #8a6d3b) so the contextual notice reads as part
// of the same warning family as the shoot banner at the top of the page.
const HiddenChildrenNotice = styled.div`
  background: #fcf8e3;
  border: 1px solid #faf2cc;
  color: #8a6d3b;
  border-radius: 4px;
  padding: 8px 12px;
  margin: 0 0 12px;
  font-size: 12px;
  line-height: 1.4;

  // ItemPanel has margin-top: 25px on desktop; pull the notice up by the
  // same amount so its top edge aligns with the SlotSelector title on the
  // left column instead of floating mid-row.
  @media (min-width: ${MOBILE_L}px) {
    margin-top: -25px;
  }
`;

// The pose layered over the backgrounds: the last pose of the latest slot.
const getLastPose = (productConfig) => {
  const lastKey = Object.keys(productConfig).sort((a, b) => b - a)[0];
  const config = productConfig[lastKey];
  const poses = config?.poses ?? (config?.pose ? [config.pose] : []);
  return Array.isArray(poses) ? poses[poses.length - 1] : config?.pose;
};

const poseUrlOf = (pose) =>
  (pose?.selected_crop ? pose.selected_crop?.url : pose?.url) || null;

const Item = ({
  src,
  largeSrc,
  onClick = () => {},
  name,
  price,
  intentGallery = {},
}) => {
  const [isZoomed, setIsZoomed] = useState(false);
  const { pathname } = useLocation();
  const { productConfig } = useSelector((state) => state.v3Order);
  const event = useSelector((state) => state.shoot);
  const lastPose = getLastPose(productConfig);
  const poseUrl = poseUrlOf(lastPose);
  const { studentPreviewImageUrl, studentId } = useSelector(
    (state) => state.additionalData,
  );
  const orders = useSelector((state) => state.v3Order.orders);
  const { getFrictionProps } = useImageIntentFriction();

  let isBackgroundSelection = false;
  // This grid is shared by every config step. Only the pose step lists the
  // child's photographs (`item.url` / crop urls); the product step lists
  // catalogue artwork (`item.image_thumbnail`) and is not reported.
  const isPoseSelection = /\/pose\//.test(pathname || '');

  const handleZoomChange = useCallback((shouldZoom) => {
    setIsZoomed(shouldZoom);
  }, []);

  const handleZoomClick = (e) => {
    e.stopPropagation();
    setIsZoomed((zoomed) => !zoomed);
  };

  if (pathname && pathname.includes('background')) {
    isBackgroundSelection = true;
  }

  let chargeForYpxBackgroundUpgrades;

  if (typeof event.chargeForYpxBackgroundUpgrades === 'boolean') {
    chargeForYpxBackgroundUpgrades = event.chargeForYpxBackgroundUpgrades;
  } else {
    chargeForYpxBackgroundUpgrades = true;
  }

  const orderContainsSingleBackgroundUpgrade =
    cartUtils.orderContainsSingleBackgroundUpgrade(orders, studentId);

  const productConfigContainsSingleBackgroundUpgrade =
    productConfigUtils.productConfigContainsSingleBackgroundUpgrade(
      productConfig,
    );

  const composedPoseUrl = poseUrl || studentPreviewImageUrl;
  // Only the composite (pose layered over the background) shows the child. A
  // bare background swatch — no pose, or a JPG pose that cannot be layered —
  // is catalogue artwork and is not reported.
  const showsComposedPose = !!composedPoseUrl && !isJpgImage(composedPoseUrl);
  // The gallery comes from the hook's Redux defaults (the order's gallery),
  // except for a photo pooled from another gallery (a sibling's, or none for
  // a group photo), which carries its own: the tile's pose (intentGallery)
  // or, on the background step, the pose layered over the background.
  const imageContext = {
    surface: 'product_config',
    // `src` is the background thumbnail here and the pose url on the pose
    // step — unique per tile either way, so tiles get their own throttle
    // bucket instead of sharing one.
    imageKey: src,
    ...(isBackgroundSelection ? imageIntentGalleryOf(lastPose) : intentGallery),
  };
  // The zoomed image renders in a portal outside this tile. On the pose step
  // it is the child's photograph and gets its own friction props (surface
  // `product_config_zoom`); elsewhere it is catalogue artwork.
  const poseZoomContent = useImageIntentZoomContent(imageContext);
  // On the background step rmiz zooms the first <img> — the bare background —
  // and React bubbles the portal's events through ControlledZoom to the tile
  // wrapper. So the friction props go on the two images inside the zoom, not
  // on the wrapper, which only blocks the right-click as before.
  const compositeProps = showsComposedPose
    ? getFrictionProps(imageContext)
    : { onContextMenu: (event) => event.preventDefault() };

  return (
    <ItemWrapper>
      {isBackgroundSelection && (
        <>
          <ItemImageBackgroundWrapper
            onContextMenu={(event) => event.preventDefault()}
          >
            <ControlledZoom isZoomed={isZoomed} onZoomChange={handleZoomChange}>
              <ItemImageBackgroundSelection
                src={src}
                alt={name}
                onClick={onClick}
                {...compositeProps}
              />
              {/* Don't layer JPG images - they don't have alpha transparency */}
              {showsComposedPose && (
                <PoseImage
                  src={composedPoseUrl}
                  alt="Pose Image"
                  onClick={onClick}
                  {...compositeProps}
                />
              )}
            </ControlledZoom>
            <ZoomButtonWrapper onClick={handleZoomClick}>
              <MagnifyingGlassPlusIcon />
            </ZoomButtonWrapper>
          </ItemImageBackgroundWrapper>
          <BackgroundNamePriceWrapper>
            <p>
              {name}
              {price &&
                !orderContainsSingleBackgroundUpgrade &&
                !productConfigContainsSingleBackgroundUpgrade &&
                chargeForYpxBackgroundUpgrades && (
                  <>
                    <br />
                    {formatUSD(price)}
                  </>
                )}
            </p>
          </BackgroundNamePriceWrapper>
        </>
      )}
      {!isBackgroundSelection && (
        <>
          <ItemImageContainer onContextMenu={(event) => event.preventDefault()}>
            <ControlledZoom
              isZoomed={isZoomed}
              onZoomChange={handleZoomChange}
              zoomImg={{ src: largeSrc }}
              ZoomContent={isPoseSelection ? poseZoomContent : undefined}
            >
              <ItemImage
                src={src}
                onClick={onClick}
                alt={name}
                {...(isPoseSelection
                  ? getFrictionProps(imageContext)
                  : { onContextMenu: (event) => event.preventDefault() })}
              />
            </ControlledZoom>
            <ZoomButtonWrapper onClick={handleZoomClick}>
              <MagnifyingGlassPlusIcon />
            </ZoomButtonWrapper>

            {event?.shoot?.company_auth_token === 'wagner' ? (
              <p>{name.length > 55 ? name.substring(0, 55) + '...' : name}</p>
            ) : (
              <p>{name.length > 24 ? name.substring(0, 24) + '...' : name}</p>
            )}
          </ItemImageContainer>
        </>
      )}
    </ItemWrapper>
  );
};

const ItemButton = ({ item, cropIndex = null, ...props }) => {
  const history = useHistory();
  // Selected here rather than threaded down: ItemButton is the only consumer on
  // this branch, and the prop had to be forwarded through ItemGrid and
  // CategoryGrid to reach it.
  const pages = useSelector(selectConfigFlowPages);
  const { productConfig } = useSelector((s) => s.v3Order);
  const { enableZoomOnProducts } = useSelector((s) => s.additionalData);
  const event = useSelector((s) => s.shoot);
  const dispatch = useDispatch();

  const handleClick = () => {
    addToConfig({
      dispatch,
      history,
      productConfig,
      pages,
      itemToAdd: item,
      event,
      cropIndex,
    });
  };

  return (
    <Item
      price={item.price || null}
      intentGallery={imageIntentGalleryOf(item)}
      {...props}
      onClick={handleClick}
    />
  );
};

// ENG-2551. Module scope on purpose: this was previously declared inside
// ConfigScreen's body, giving it a new identity every parent render — React
// remounted it each time and silently reset the selected category. Exported
// for direct unit testing (categoryGrid.test.jsx).
export const CategoryGrid = ({ categories = [], renderItems }) => {
  // Track the selection by index: names are studio-authored and not unique
  // (two sets can both be "Studio"), and Background.tsx rebuilds these category
  // objects from shoot data on every render, so neither the name nor the
  // object identity distinguishes one set from another.
  const [selectedIndex, setSelectedIndex] = useState(0);
  const isPhone = useIsPhone();

  // One set is not a choice: nothing to collapse, and no picker once
  // collapsed. On phones the lone button goes too, so the customer lands on
  // the thumbnails; >=768px still renders it exactly as it always has.
  const hasChoice = categories.length > 1;
  const showSelector = hasChoice || !isPhone;

  // Phones land folded: the picker opens collapsed on the first set, and a
  // selection re-folds and scrolls that set's first row into view.
  const { folded, fold, unfold, gridRef } = useFoldOnSelect({
    foldByDefault: hasChoice,
  });

  const selectCategory = (_category, index) => {
    setSelectedIndex(index);
    if (hasChoice) fold();
  };

  // An index left over from a longer category list falls back to the first.
  const visibleCategory = categories[selectedIndex] ?? categories[0];

  // A shoot with no background categories has nothing to pick from. `[]` is
  // truthy at the call site, so without this we would dereference an absent
  // category and crash the step.
  if (!visibleCategory) {
    return (
      <CategoryFoldWrapper ref={gridRef}>{renderItems([])}</CategoryFoldWrapper>
    );
  }

  return (
    <CategoryFoldWrapper ref={gridRef}>
      {folded ? (
        <CategoryPicker
          categories={categories}
          selectedIndex={selectedIndex}
          onSelect={selectCategory}
          onExpand={unfold}
        />
      ) : !showSelector ? null : (
        <CategorySelector>
          {categories.map((c, index) => (
            <CategoryButton
              key={c.id ?? index}
              selected={index === selectedIndex}
              onClick={() => selectCategory(c, index)}
            >
              {c.name}
            </CategoryButton>
          ))}
        </CategorySelector>
      )}
      {renderItems(visibleCategory.items)}
    </CategoryFoldWrapper>
  );
};

export const ConfigScreen = ({
  items,
  categories,
  itemsDesktop,
  itemsMobile,
  instruction,
  gridStyle = {},
  children,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const shoot = useSelector((s) => s.shoot);
  const additionalData = useSelector((s) => s.additionalData);
  const [screenSize, loadingScreenSize] = useScreenSize();
  const hash = location.hash?.replace('#', '');
  const product = getProductByLocation(
    shoot?.shootProducts,
    location,
    extraConfigProducts(shoot),
  );
  const parent = useSelector((s) => s.parent);
  const restricted = isShippingRestrictedForParent(shoot?.shoot, parent);
  const showHiddenNotice = ypxHasHiddenChildren(product, restricted);
  const { getFrictionProps } = useImageIntentFriction({
    surface: 'product_config',
  });

  const {
    additional_data = {},
    additional_data: { free_indices = [] } = {},
    required_children_count,
  } = product;

  const isYouPickX = Object.keys(additional_data).length == 0;
  const isBYO = Object.keys(additional_data).length > 0;

  const { name, price, maximum_children_count: slotCount } = product;
  const hasHash = !!hash;
  const pages = useSelector(selectConfigFlowPages);
  const { productConfig } = useSelector((s) => s.v3Order);
  const { control, handleSubmit, register, setValue } = useForm();
  const [requiredProductFields, setRequiredProductFields] = useState([]);
  const selectedProductOptions = useSelector(
    (s) => s.productConfig.selectedProductOptions,
  );

  useEffect(() => {
    const currentUrlSplit = history.location.pathname.split('/');
    const currentSection = currentUrlSplit[currentUrlSplit.length - 2];
    const currentIndex = parseInt(
      currentUrlSplit[currentUrlSplit.length - 1]?.replace('#slots', ''),
    );
    const item = productConfig[currentIndex];
    let requiredFieldsFromAllProducts = [];
    Object.keys(productConfig).forEach((key, index) => {
      if (productConfig[key].product?.required_product_fields?.length > 0) {
        requiredFieldsFromAllProducts.push(
          productConfig[key].product?.required_product_fields,
        );
      }
    });

    const allFields = requiredFieldsFromAllProducts.flat();
    const uniqueFields = allFields.filter(
      (field, index) =>
        allFields.findIndex((f) => f.key === field.key) === index,
    );
    setRequiredProductFields(uniqueFields);
    if (item && item[currentSection])
      dispatch(popItemFromConfig(currentIndex, currentSection));
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  // Keep the Redux currentProduct in sync with the product derived from the
  // URL so ProductOptionsSelector (which reads currentProduct) renders the
  // option sets for the product actually being configured. setCurrentProduct
  // also seeds/reconciles the default option selections.
  useEffect(() => {
    dispatch(setCurrentProduct(product));
  }, [product, dispatch]);

  const shouldShowSlots = screenSize !== 'Mobile' || hasHash;
  const shouldShowItems = screenSize !== 'Mobile' || !hasHash;

  // For pose screen with multiple poses: current slot index and selected poses
  const pathParts = location.pathname.split('/').filter(Boolean);
  const currentSection = pathParts[pathParts.length - 2];
  const currentSlotIndex = parseInt(
    pathParts[pathParts.length - 1]?.replace('#slots', '') || '0',
    10,
  );
  const isPoseScreen = currentSection === 'pose';
  const currentSlotConfig = productConfig[currentSlotIndex];
  // This wizard's pose step only knows the individual gallery — it has no
  // group-image source, so a child with a group pose slot (pure group or a
  // multiple-image child with a group node) would silently offer the wrong
  // photos. Guard with an explanation instead.
  const slotProductUnsupported =
    isPoseScreen && productHasGroupRecord(currentSlotConfig?.product);
  const posesCountForSlot =
    typeof currentSlotConfig?.product?.poses_count === 'number'
      ? currentSlotConfig.product.poses_count
      : 1;
  const selectedPosesForSlot = isPoseScreen
    ? getPosesForSlot(currentSlotConfig)
    : [];

  // On pose screen with multiple poses per slot, hide poses already selected for this slot
  const selectedPoseIds = (selectedPosesForSlot || [])
    .map((p) => p?.id)
    .filter(Boolean);
  const itemsToShow =
    isPoseScreen && selectedPoseIds.length > 0
      ? items.filter((item) => !selectedPoseIds.includes(item.id))
      : items;

  const products = useSelector(selectConfiguredChildProducts);

  const background_choice_mode = useSelector(
    (state) => state.shoot?.shoot?.background_choice_mode,
  );
  const isGreenScreenEvent =
    background_choice_mode === 'advanced_greenscreen_backgrounds_only';

  // Support both single .pose and multiple .poses per slot
  const poses = Object.values(productConfig).reduce(
    (a, b) => [...a, ...getPosesForSlot(b)],
    [],
  );

  // Never below 1: required_children_count is an integer on every parent row
  // and defaults to 0, so taking it verbatim made this 0 for any package left
  // at the default. That enabled Add to Cart with nothing chosen and let
  // submitOrder's guard through, which is how an empty package reached the
  // cart in the first place (ENG-1683). A package always needs at least one
  // item; addToCart refuses zero regardless, so leaving the button live only
  // traded a disabled button for a dead-end alert.
  const minimumChildCount = Math.max(
    1,
    isYouPickX && Number.isInteger(required_children_count)
      ? required_children_count
      : isYouPickX
      ? slotCount
      : 1,
  );

  // Per-slot pose requirement: each slot with a product must have its required number of poses (0, 1, or 2)
  const allPosesSatisfied = Object.values(productConfig).every((config) => {
    const required = getRequiredPoseCountForSlot(config, pages);
    return getPosesForSlot(config).length >= required;
  });
  // Asked per slot, not as a count: a background belonging to a child that cannot
  // use one must never satisfy the requirement of a child that still owes one.
  // See selectBackgroundsSatisfied.
  const allBackgroundsSatisfied = useSelector(selectBackgroundsSatisfied);

  const actionButtonEnabled =
    allPosesSatisfied &&
    allBackgroundsSatisfied &&
    products.length >= minimumChildCount;

  const submitOrder = () => {
    handleSubmit((formState) => {
      // TODO: handle poses

      if (isBYO) {
        if ([...products].length < minimumChildCount)
          return Swal.fire(
            `Please configure at least ${minimumChildCount} item(s).`,
          );

        if (!allBackgroundsSatisfied && isGreenScreenEvent)
          return Swal.fire(`Please finish configuring all items.`);
      }

      if (isYouPickX && !actionButtonEnabled) {
        return Swal.fire(
          `Please configure ${minimumChildCount} ${pluralize(
            'item',
            minimumChildCount,
          )}.`,
        );
      }

      dispatch(
        addToCart({
          studentId: additionalData.studentId,
          fullproductData: product,
          requiredFields: formState,
          productOptions: selectedProductOptions,
        }),
      );
    })();
  };

  if (loadingScreenSize) return <div />;

  const ItemGrid = ({ itemsDesktop, itemsMobile, items, gridStyle = {} }) => (
    <ItemsGrid
      itemsDesktop={itemsDesktop}
      itemsMobile={itemsMobile}
      style={gridStyle}
    >
      {items.map((item) => {
        if (item.cropped_images?.length > 0) {
          return item.cropped_images.map((image, i) => (
            <ItemButton
              key={`cropped-img-${image.id}`}
              id={item.id}
              name={image.name}
              src={image.url}
              shoot={shoot}
              item={item}
              cropIndex={i}
            />
          ));
        }
        return (
          <ItemButton
            key={item.id}
            id={item.id}
            name={item.display_name || item.name}
            src={item.image_thumbnail || item.url}
            largeSrc={item.image_large || null}
            shoot={shoot}
            item={item}
          />
        );
      })}
    </ItemsGrid>
  );

  return (
    <>
      <ConfigScreenWrapper>
        {shouldShowSlots && (
          <SlotSelector instruction={instruction} handleSubmit={handleSubmit} />
        )}
        {shouldBreakConfigLoop(
          shoot.shootProducts,
          location,
          extraConfigProducts(shoot),
        ) && shouldShowItems ? (
          <></>
        ) : shouldShowItems ? (
          <ItemPanel>
            {screenSize !== 'Desktop' && (
              <h3>
                {name} <br />
                <Instruction>{instruction}</Instruction>
              </h3>
            )}
            {showHiddenNotice && (
              <HiddenChildrenNotice role="status">
                Some items in this bundle aren't available for shipping outside
                the US and have been hidden.
              </HiddenChildrenNotice>
            )}
            {slotProductUnsupported ? (
              <UnsupportedProductConfig
                productName={currentSlotConfig?.product?.name}
              />
            ) : children ? (
              children
            ) : categories ? (
              <CategoryGrid
                categories={categories}
                renderItems={(categoryItems) => (
                  <ItemGrid
                    itemsDesktop={itemsDesktop}
                    itemsMobile={itemsMobile}
                    gridStyle={gridStyle}
                    items={categoryItems}
                  />
                )}
              />
            ) : (
              <>
                {isPoseScreen && posesCountForSlot > 1 && (
                  <PoseSlotsRow
                    itemsDesktop={itemsDesktop}
                    itemsMobile={itemsMobile}
                  >
                    {Array.from({ length: posesCountForSlot }).map((_, i) => {
                      if (!selectedPosesForSlot[i]) {
                        return <PoseSlotBox key={i}>Pose {i + 1}</PoseSlotBox>;
                      }

                      const slotPoseUrl =
                        selectedPosesForSlot[i].selected_crop?.url ||
                        selectedPosesForSlot[i].url;
                      const slotContext = {
                        surface: 'product_config',
                        imageKey: slotPoseUrl || `pose-slot-${i}`,
                        ...imageIntentGalleryOf(selectedPosesForSlot[i]),
                      };

                      return (
                        <PoseSlotBoxFilled key={i}>
                          <img
                            src={slotPoseUrl}
                            alt={`Pose ${i + 1}`}
                            {...getFrictionProps(slotContext)}
                          />
                        </PoseSlotBoxFilled>
                      );
                    })}
                  </PoseSlotsRow>
                )}
                <ItemGrid
                  itemsDesktop={itemsDesktop}
                  itemsMobile={itemsMobile}
                  gridStyle={gridStyle}
                  items={itemsToShow}
                />
              </>
            )}
          </ItemPanel>
        ) : (
          <div />
        )}
        {shouldBreakConfigLoop(
          shoot.shootProducts,
          location,
          extraConfigProducts(shoot),
        ) &&
          (requiredProductFields.length > 0 ? (
            <DoneWrapper>
              <RequiredProductForm
                requiredFields={requiredProductFields}
                register={register}
                control={control}
                setValue={setValue}
              />
              <ProductOptionsSelector />
              <DoneScreen product={product} onDone={submitOrder} />
            </DoneWrapper>
          ) : (
            <DoneWrapper>
              <ProductOptionsSelector />
              <DoneScreen product={product} onDone={submitOrder} />
            </DoneWrapper>
          ))}
      </ConfigScreenWrapper>
    </>
  );
};

export default ConfigScreen;
